import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import OrderItemLabels, { PDFViewBrowser, PDFViewBrowserCourier, PDFViewBrowserShipment } from './content/applications/Orders/OrderShippingLabel';
import { PDFViewer } from '@react-pdf/renderer';
import DailyKPIs from './content/applications/Orders/DailyKPIs';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const Crypto = Loader(lazy(() => import('src/content/dashboards/Crypto')));

// Applications

const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);
const Transactions = Loader(
  lazy(() => import('src/content/applications/Transactions'))
);
const Orders = Loader(
  lazy(() => import('src/content/applications/Orders'))
);
const UniformOrders = Loader(
  lazy(() => import('src/content/applications/UniformOrders'))
);
const OrdersProduction = Loader(
  lazy(() => import('src/content/applications/OrdersProduction'))
);
const OrdersProductionCutting = Loader(
  lazy(() => import('src/content/applications/OrdersProductionCutting'))
);
const OrdersProductionSewing = Loader(
  lazy(() => import('src/content/applications/OrdersProductionSewing'))
);
const OrdersProductionPicking = Loader(
  lazy(() => import('src/content/applications/OrdersProductionPicking'))
);
const CeosPanel = Loader(
  lazy(() => import('src/content/applications/OrdersProductionCeosPanel'))
);
const OrdersProductionFinishing = Loader(
  lazy(() => import('src/content/applications/OrdersProductionFinishing'))
);
const InventoryRequests = Loader(
  lazy(() => import('src/content/applications/InventoryRequests'))
);
const ClothPieces = Loader(
  lazy(() => import('src/content/applications/ClothPieces'))
);
const ProductList = Loader(
  lazy(() => import('src/content/applications/ProductList'))
);
const RegularSizeStock = Loader(
  lazy(() => import('src/content/applications/RegularSizeStock'))
);
const UnitItems = Loader(
  lazy(() => import('src/content/applications/UnitItems'))
);
const Midstock = Loader(
  lazy(() => import('src/content/applications/Midstock'))
);
const ClothInventory = Loader(
  lazy(() => import('src/content/applications/ClothInventory'))
);
const FileList = Loader(
  lazy(() => import('src/content/applications/FileList'))
);
const EmbroideryStudio = Loader(
  lazy(() => import('src/content/applications/EmbroideryStudio'))
);
const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);
const UserList = Loader(
  lazy(() => import('src/content/applications/UserList'))
);
const ServiceTickets = Loader(
  lazy(()  => import('src/content/applications/ServiceTickets'))
);
const WorkPoints = Loader(
  lazy(()  => import('src/content/applications/WorkPoints'))
);
const WorkBatchCut = Loader(
  lazy(()  => import('src/content/applications/WorkBatchCut'))
);
const OrderItemFulfill = Loader(
  lazy(()  => import('src/content/applications/OrderItemFulfill'))
);
const WorkPointManual = Loader(
  lazy(()  => import('src/content/applications/WorkPointManual'))
);
const OrdersCharts = Loader(
  lazy(()  => import('src/content/applications/OrdersCharts'))
);
const ProductionKpiByDate = Loader(
  lazy(()  => import('src/content/applications/ProductionKpiByDate'))
);
const LayarQCPickup = Loader(
  lazy(()  => import('src/content/applications/LayarQCPickup'))
);
const CategoryReviews = Loader(
  lazy(()  => import('src/content/applications/CategoryReview'))
);
const CollectionList = Loader(
  lazy(()  => import('src/content/applications/CollectionList'))
);
const ProductAnalytics = Loader(
  lazy(()  => import('src/content/applications/ProductAnalytics'))
);
const ProductFlashSale = Loader(
  lazy(()  => import('src/content/applications/ProductFlashSale'))
);
const EmployeeStatusPage = Loader(
  lazy(() => import('src/content/applications/Employee'))
);
const SalesPage = Loader(
  lazy(() => import('src/content/applications/Sales'))
)
const RetentionPage = Loader(
  lazy(() => import('src/content/applications/Retention'))
)
const MetaAdsPage = Loader(
  lazy(() => import('src/content/applications/MetaAds'))
)
const BroadcastPage = Loader(
  lazy(() => import('src/content/applications/Broadcast'))
)
const BroadcastCampaignDetailPage = Loader(
  lazy(() => import('src/content/applications/Broadcast/CampaignDetail'))
)
const SavedAudiencesPage = Loader(
  lazy(() => import('src/content/applications/Marketing/AudienceList'))
)
const BlastSettingsPage = Loader(
  lazy(() => import('src/content/applications/Marketing/BlastSettingList'))
)
const MessageTemplatesPage = Loader(
  lazy(() => import('src/content/applications/Marketing/MessageTemplateList'))
)
const WaAccountsPage = Loader(
  lazy(() => import('src/content/applications/Marketing/WaAccountList'))
)
const BranchPricingPage = Loader(
  lazy(() => import('src/content/applications/BranchPricing'))
)
const EmployeeCreatePage = Loader(
  lazy(() => import('src/content/applications/EmployeeCreate'))
);
const Accounting = Loader(
  lazy(() => import('src/content/applications/Accounting'))
);


// Components

const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes: RouteObject[] = [
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Orders />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'monitor-produksi',
    children: [
      {
        path: 'monitor-cutting',
        element: <OrdersProductionCutting />
      },
      {
        path: 'monitor-finishing',
        element: <OrdersProductionFinishing />
      },
      {
        path: 'monitor-sewing',
        element: <OrdersProductionSewing />
      },
      {
        path: 'monitor-picking',
        element: <OrdersProductionPicking />
      },
      {
        path: 'ceos-panel',
        element: <CeosPanel />
      },
    ]
  },
  {
    path: 'orders-and-requests',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="orders" replace />
      },
      {
        path: 'orders',
        element: <Orders />
      },
      {
        path: 'uniform-orders',
        element: <UniformOrders />
      },
      {
        path: 'order-harus-kirim',
        element: <OrdersProduction />
      },
      {
        path: 'inventory-requests',
        element: <InventoryRequests />
      },
      {
        path: 'service-tickets',
        element: <ServiceTickets />
      },
      {
        path: 'work-points',
        element: <WorkPoints />
      },
      {
        path: 'work-batch-cut',
        element: <WorkBatchCut />
      },
      {
        path: 'order-item-fulfill',
        element: <OrderItemFulfill />
      },
      {
        path: 'work-point-manual',
        element: <WorkPointManual />
      },
      {
        path: 'prod-charts',
        element: <OrdersCharts />
      },
      {
        path: 'prod-kpi-by-date',
        element: <ProductionKpiByDate />
      },
      {
        path: 'sales',
        element: <SalesPage />
      },
      {
        path: 'customer-retention',
        element: <RetentionPage />
      },
      {
        path: 'meta-ads',
        element: <MetaAdsPage />
      },
      {
        path: 'broadcast',
        element: <Navigate to="/marketing/broadcast" replace />
      },
      {
        path: 'branch-pricing',
        element: <BranchPricingPage />
      },
      {
        path: 'layar-qc-pickup',
        element: <LayarQCPickup />
      },
    ]
  },
  {
    path: 'marketing',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="saved-audiences" replace />
      },
      {
        path: 'saved-audiences',
        element: <SavedAudiencesPage />
      },
      {
        path: 'blast-settings',
        element: <BlastSettingsPage />
      },
      {
        path: 'message-templates',
        element: <MessageTemplatesPage />
      },
      {
        path: 'wa-accounts',
        element: <WaAccountsPage />
      },
      {
        path: 'broadcast',
        element: <BroadcastPage />
      },
      {
        path: 'broadcast/:campaignId',
        element: <BroadcastCampaignDetailPage />
      },
    ]
  },
  {
    path: 'accounting',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="general-ledger" replace />
      },
      {
        path: 'general-ledger',
        element: <Accounting />
      },
    ]
  },
  {
    path: 'daily-kpis',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="revenue" replace />
      },
      {
        path: 'revenue',
        element: <DailyKPIs />
      },
    ]
  },
  {
    path: 'inventory',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="cloth-pieces" replace />
      },
      {
        path: 'cloth-pieces',
        element: <ClothPieces />
      },
      {
        path: 'unit-items',
        element: <UnitItems />
      },
      {
        path: 'regular-size-stock',
        element: <RegularSizeStock />
      },
      {
        path: 'midstock',
        element: <Midstock />
      },
      {
        path: 'cloth-inventory',
        element: <ClothInventory />
      },
    ]
  },
  {
    path: 'products',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="product-list" replace />
      },
      {
        path: 'product-list',
        element: <ProductList />
      },
      {
        path: 'user-list',
        element: <UserList />
      },
      {
        path: 'category-reviews',
        element: <CategoryReviews />
      },
      {
        path: 'collection-list',
        element: <CollectionList />
      },
      {
        path: 'analytics',
        element: <ProductAnalytics />
      },
      {
        path: 'product-flash-sale',
        element: <ProductFlashSale />
      }
    ]
  },
  {
    path: 'files',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="files-list" replace />
      },
      {
        path: 'files-list',
        element: <FileList />
      },
      {
        path: 'embroidery-digitizer',
        element: <EmbroideryStudio />
      },
    ]
  },
  {
    path: '/components',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  },
  {
    path: '/preview-sticker',
    element: <PDFViewBrowser />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  },
  {
    path: '/preview-sticker-shipment',
    element: <PDFViewBrowserShipment />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  },
  {
    path: '/preview-sticker-courier',
    element: <PDFViewBrowserCourier />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  },
  {
    path: '/employee',
    element: <SidebarLayout />,
    children: [
      {
        path: 'status',
        element: <EmployeeStatusPage />
      },
      {
        path: 'add-employee',
        element: <EmployeeCreatePage />
      }
    ]
  }
];

export default routes;
